import { Component, ChangeDetectionStrategy } from '@angular/core';
import { AuthService, Unity4Claim, LayoutService } from '@adreno-common';
import { merge, of } from 'rxjs';
import { map, scan, take } from 'rxjs/operators';
import { GetUserProfileVm, TeleworkerState, UserProfile } from '../models';
import { CoreService, UserProfileService } from '../services';
import { ToastrService } from 'ngx-toastr';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { StatsModalComponent } from '@app/work/components';
import { Router } from '@angular/router';

@Component({
  selector: 'app-user-profile',
  template: `
    <ng-container *ngIf="viewModel$ | async as vm">
      <li
        class="nav-item"
        ngbDropdown
        placement="bottom-right"
        *ngIf="!!vm.profile"
      >
        <a
          ngbDropdownToggle
          class="nav-link text-muted waves-effect waves-dark pro-pic"
          href="javascript:void(0)"
          data-toggle="dropdown"
          aria-haspopup="true"
          aria-expanded="false"
        >
          <img
            [src]="vm.userId | userAvatar | async"
            alt="user"
            class="rounded-circle small-avatar"
            width="31"
          />
        </a>
        <div class="dropdown-menu-right user-dd" ngbDropdownMenu>
          <div class="d-flex no-block align-items-center p-15 m-b-10">
            <div class="">
              <img
                [src]="vm.userId | userAvatar: true | async"
                alt="user"
                class="rounded large-avatar"
                width="70"
              />
            </div>
            <div class="m-l-10">
              <h4 class="m-b-0 font-15">
                {{ vm.profile.first }} {{ vm.profile.last }}
              </h4>
              <p class="text-muted m-b-0 font-12">{{ vm.profile.email }}</p>
            </div>
          </div>
          <a class="dropdown-item" (click)="stats()">
            <i class="fas fa-list-alt m-r-5 m-l-5"></i> Outcome Stats</a
          >
          <a
            *libHasClaim="Unity4Claim.ContactDetailsModule"
            class="dropdown-item"
            (click)="contactdetails()"
          >
            <i class="far fa-address-card m-r-5 m-l-5"></i> Contact Details</a
          >
          <a
            *libHasClaim="Unity4Claim.ClickToCall"
            class="dropdown-item"
            (click)="clickToCall()"
          >
            <i class="fas fa-phone-volume m-r-5 m-l-5"></i> Click to Call</a
          >
          <div class="dropdown-divider"></div>
          <a class="dropdown-item" (click)="toggleDarkMode()">
            <i class="far fa-moon m-r-5 m-l-5"></i> Toggle DarkMode</a
          >
          <div class="dropdown-divider"></div>
          <a class="dropdown-item" (click)="logout()">
            <i class="fa fa-power-off m-r-5 m-l-5"></i> Logout</a
          >
        </div>
      </li>
    </ng-container>
  `,
  styles: [
    `
      .small-avatar {
        min-width: 40px;
        max-width: 40px;
        min-height: 40px;
        max-height: 40px;
        overflow: hidden;
      }

      .large-avatar {
        min-width: 70px;
        max-width: 70px;
        min-height: 70px;
        max-height: 70px;
        overflow: hidden;
      }
    `,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UserProfileComponent {
  Unity4Claim = Unity4Claim;

  viewModel$ = merge(
    this.authSvc.userId$.pipe(map((userId) => ({ userId }))),
    this.userProfileSvc.userProfile$.pipe(map((profile) => ({ profile })))
  ).pipe(scan((acc, value) => ({ ...acc, ...value }), {} as ViewModel));

  constructor(
    private layoutSvc: LayoutService,
    private userProfileSvc: UserProfileService,
    private authSvc: AuthService,
    private toastr: ToastrService,
    private modalSvc: NgbModal,
    private router: Router,
    private coreSvc: CoreService
  ) {
    this.userProfileSvc.userProfile$.subscribe((profile) => {
      if (profile !== undefined && profile.twDarkMode === true)
        this.layoutSvc.setDarkMode();
    });
  }

  clickToCall() {
    this.userProfileSvc.clickToCall();
  }

  contactdetails() {
    if (this.router.url.startsWith('/work')) {
      this.toastr.info('Exit to the portal to access Contact Details.', '', {
        timeOut: 1000,
      });
    } else {
      this.router.navigate(['/contact-details']);
    }
  }

  logout() {
    if (this.router.url.startsWith('/work') === false) {
      this.coreSvc.state = TeleworkerState.Logout;
      this.authSvc.signOut();
    } else {
      this.toastr.info('You must select exit before logging out.');
    }
  }

  stats() {
    this.coreSvc.state$.pipe(take(1)).subscribe((currentState) => {
      if (
        [
          TeleworkerState.WrapIB,
          TeleworkerState.WrapOB,
          TeleworkerState.Unavailable,
          TeleworkerState.UnavailableOB,
          TeleworkerState.UnavailableIB,
          TeleworkerState.Reports,
          TeleworkerState.Timesheets,
          TeleworkerState.Training,
          TeleworkerState.ContactDetails,
          TeleworkerState.Work,
          TeleworkerState.DiallerLogout,
          TeleworkerState.News,
        ].includes(currentState!)
      ) {
        this.modalSvc.open(StatsModalComponent, {
          size: 'lg',
        });
      } else {
        this.toastr.info('Outcome Stats unavailable whilst handling calls.');
      }
    });
  }

  toggleDarkMode() {
    this.layoutSvc.toggleDarkMode();
    this.userProfileSvc.toggleDarkMode();
  }
}

interface ViewModel {
  profile: UserProfile;
  userId?: number;
}
